import { Link } from 'react-router-dom';
import { ChevronRight, Cpu, Layers } from 'lucide-react';
import { useRunGroups } from '../hooks/useRunGroups';
import { desktopDeepLinks } from '../lib/deep-links';
import { DeepLinkButton, StatusPill } from '../components/operator-ui';

/**
 * WI-14 (OPERATOR-UI-PLACEMENT): thin operator console.
 *
 * This view ONLY reads execution state (run groups, workers, attempts, evidence,
 * reconciliation) and hands the operator off to the native Aldan Desktop console
 * via typed deep links. It deliberately embeds nothing and reimplements no native
 * surface — every actionable control is an "Open in Desktop" deep link.
 *
 * The list routes each run group to its detail view
 * (`/operator/run-groups/:id`), where attempts, evidence, and reconciliation are
 * inspected read-only.
 */
export default function OperatorConsole() {
  const { data: runGroups, isLoading } = useRunGroups();

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Operator Console</h1>
        <p className="mt-1 text-sm text-slate-500">
          Read-only view of run groups, workers, attempts, evidence, and reconciliation. Actions
          open in the Aldan Desktop console.
        </p>
      </div>

      {isLoading && <div className="text-sm text-slate-400">Loading run groups…</div>}

      {!isLoading && (runGroups?.length ?? 0) === 0 && (
        <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
          No run groups
        </div>
      )}

      <div className="space-y-2">
        {runGroups?.map((rg) => (
          <div
            key={rg.id}
            className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4"
          >
            <Link
              to={`/operator/run-groups/${rg.id}`}
              className="group flex min-w-0 flex-1 items-center gap-3"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
                <Layers className="h-4 w-4 text-slate-600" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate font-mono text-sm text-slate-900 group-hover:underline">
                    {rg.id}
                  </span>
                  <StatusPill status={rg.status} />
                </div>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  generation {rg.generation}
                  {rg.projectedGeneration != null && <> · projected {rg.projectedGeneration}</>}
                  {rg.workerId && (
                    <>
                      {' '}
                      · <Cpu className="inline h-3 w-3 text-slate-400" /> worker {rg.workerId}
                    </>
                  )}
                </p>
              </div>
            </Link>
            <div className="flex flex-shrink-0 items-center gap-2">
              <DeepLinkButton href={desktopDeepLinks.runGroup(rg.id)} label="Open run group" />
              <Link
                to={`/operator/run-groups/${rg.id}`}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:text-slate-900"
              >
                Detail
                <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
